import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Star, MapPin, ShieldCheck, ArrowRight, Loader2, Globe2 } from 'lucide-react';
import { REGIONS } from '../config/regions';
import { useSEO, buildBreadcrumbSchema, buildFaqSchema } from '../utils/seo';
import Header from '../components/Header';
import PublicTrustStrip from '../components/PublicTrustStrip';
import PulseReportCTA from '../components/PulseReportCTA';
import StickyMobileCTA from '../components/StickyMobileCTA';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BASE_URL = 'https://shopperden.com';

/**
 * Regional aggregator — `/region/:slug`.
 *
 * Hits /api/pro-leads once per city listed in the region config, merges
 * the results, and renders one indexable landing page. Cross-links to
 * the city-specific /pros/{city} pages so the internal-link graph
 * compounds (Google rewards interlinked clusters).
 */
const RegionHubPage = () => {
  const { slug } = useParams();
  const cfg = REGIONS[slug];
  const [proBundles, setProBundles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!cfg) return;
    (async () => {
      setLoading(true);
      const results = await Promise.all(
        cfg.cities.map(async (city) => {
          try {
            const params = new URLSearchParams({ city, country: cfg.country, limit: '6' });
            const r = await axios.get(`${API_URL}/api/pro-leads?${params}`);
            return { city, items: r.data?.items || [], unlock_fee: r.data?.unlock_fee };
          } catch {
            return { city, items: [], unlock_fee: null };
          }
        }),
      );
      if (!cancelled) {
        setProBundles(results);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cfg]);

  const canonical = `${BASE_URL}/region/${slug}`;
  const totalPros = proBundles.reduce((n, b) => n + (b.items?.length || 0), 0);
  const fee = proBundles.find((b) => b.unlock_fee)?.unlock_fee;
  const feeStr = fee ? `${fee.currency === 'inr' ? '₹' : fee.currency === 'cad' ? 'C$' : '£'}${fee.amount}` : '£15.52';
  const description = cfg
    ? `${cfg.sub} ${totalPros} professionals indexed across ${cfg.cities.join(', ')}. Flat ${feeStr} connection fee.`
    : '';

  useSEO({
    title: cfg ? cfg.h1 : 'Shopperden',
    description,
    canonical,
    type: 'website',
    schema: cfg ? [
      buildBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Regions', url: '/find-a-professional' },
        { name: cfg.name, url: `/region/${cfg.slug}` },
      ]),
      buildFaqSchema(cfg.faqs),
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: cfg.h1,
        description,
        url: canonical,
        about: cfg.cities.map((c) => ({ '@type': 'Place', name: c, address: { '@type': 'PostalAddress', addressCountry: cfg.country } })),
      },
    ] : null,
  });

  if (!cfg) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid={`region-${cfg.slug}`}>
      <Header />
      <PublicTrustStrip />

      <section className="bg-gradient-to-b from-[#FEF8EC] via-white to-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-bold mb-3 flex items-center gap-2">
            <Globe2 className="h-3.5 w-3.5" /> Regional hub · {cfg.country}
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-5xl text-[#0F172A] leading-tight max-w-3xl" data-testid="region-h1">
            {cfg.h1}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-3 max-w-2xl leading-relaxed">{cfg.sub}</p>
          <div className="flex flex-wrap gap-2 mt-5" data-testid="region-city-chips">
            {cfg.cities.map((c) => (
              <Link
                key={c}
                to={`/pros/${c.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-xs border border-slate-300 hover:border-[#E07A3A] hover:text-[#E07A3A] px-3 py-1.5 rounded-full transition-colors"
                data-testid={`region-chip-${c.toLowerCase()}`}
              >
                {c}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 text-center">
          <p className="text-sm text-slate-600 italic leading-relaxed">{cfg.audience_blurb}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          proBundles.map((b) => (
            <div key={b.city} className="mb-10" data-testid={`region-city-block-${b.city.toLowerCase()}`}>
              <div className="flex items-end justify-between mb-4 gap-3">
                <div>
                  <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-bold">Top pros in</p>
                  <h2 className="font-['Playfair_Display'] text-xl sm:text-2xl text-[#0F172A]">{b.city}</h2>
                </div>
                <Link
                  to={`/pros/${b.city.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-xs font-semibold text-[#E07A3A] hover:text-[#C4652E] inline-flex items-center gap-1"
                  data-testid={`region-viewall-${b.city.toLowerCase()}`}
                >
                  See all {b.city} pros <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              {b.items.length === 0 ? (
                <p className="text-sm text-slate-500">No pros indexed yet for {b.city} — check back after the next harvest cycle.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {b.items.slice(0, 6).map((it) => (
                    <article
                      key={it.place_id}
                      className="border border-slate-200 rounded p-4 bg-white hover:shadow-md transition-shadow"
                      data-testid={`region-pro-${it.place_id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] tracking-[0.2em] text-[#E07A3A] uppercase font-semibold mb-1">
                            {(it.category || '').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </p>
                          <h3 className="font-semibold text-[#0F172A] truncate">{it.name}</h3>
                          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />{it.city}
                          </p>
                        </div>
                        {it.rating && (
                          <div className="flex flex-col items-end flex-shrink-0">
                            <div className="flex items-center gap-1 text-amber-600">
                              <Star className="h-3.5 w-3.5 fill-current" />
                              <span className="text-sm font-bold">{Number(it.rating).toFixed(1)}</span>
                            </div>
                            <span className="text-[10px] text-slate-400">{it.review_count || 0} reviews</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <Link
                          to={`/claim/${it.place_id}`}
                          className="text-xs font-semibold text-[#E07A3A] hover:text-[#C4652E] inline-flex items-center gap-1"
                          data-testid={`region-claim-${it.place_id}`}
                        >
                          Is this you? Claim profile <ArrowRight className="h-3 w-3" />
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </section>

      <PulseReportCTA
        role={cfg.slug === 'punjab' ? 'nri' : 'buyer'}
        city={cfg.cities[0]}
        country={cfg.country}
        dataTestId={`pulse-cta-region-${cfg.slug}`}
      />

      <section className="bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-bold mb-3">FAQs</p>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-6">{cfg.name} property — what NRIs ask</h2>
          <div className="space-y-2">
            {cfg.faqs.map((f, i) => (
              <details key={i} className="border border-slate-200 rounded p-4 open:bg-slate-50/60" data-testid={`region-faq-${i}`}>
                <summary className="cursor-pointer font-semibold text-[#0F172A] text-sm">{f.q}</summary>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0F172A] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-center">
          <ShieldCheck className="h-8 w-8 text-[#E07A3A] mx-auto mb-3" />
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl mb-3">Are you a {cfg.name} pro?</h2>
          <p className="text-sm text-slate-300 max-w-md mx-auto mb-5">
            Claim your free Featured Pro profile. NRI buyers in UK & Canada are unlocking pro contacts here every week.
          </p>
          <Link
            to="/for/professionals"
            className="inline-flex items-center gap-2 bg-[#E07A3A] hover:bg-[#C4652E] text-white text-sm tracking-[0.2em] font-bold uppercase px-6 py-3"
            data-testid="region-pros-cta"
          >
            How it works for pros
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <StickyMobileCTA
        label={`Unlock pros across ${cfg.name}`}
        ctaText="Browse"
        to="/auth?role=buyer"
        dataTestId={`sticky-cta-region-${cfg.slug}`}
      />
    </div>
  );
};

export default RegionHubPage;
